// The cloud-top corridor view: 04-body-data-ladder.md's class (c).
//
// A gas or ice giant has no ground, so it gets no hex layer, no plot lattice
// and no rover. What it gets instead is what canon asks for
// (atlas-00-architecture.md 5.4): the limb in side view with three labelled
// depth strata over it, the one the camera is inside lit, the He-3 annotation
// canon publishes, and the Gens Iovia scoop perijove marked.
//
// The strata are drawn at their TRUE radii - the deck plus each band's own edge
// off the registry row - and nothing here is exaggerated to make them legible.
// What makes them legible is where the camera is: the descent ends inside the
// corridor, so the shells are around the eye and the path length through each
// one grows toward the limb, which is the band structure seen edge-on. Face-on
// they nearly vanish, and that is correct - 300 km of air over 69,911 km of
// planet is 0.4 percent of the radius.
//
// Cloud imagery is out of scope: the globe under this draws whatever the row's
// `texture` gives it, which for all four giants is currently nothing, so it
// draws `albedo_color` (docs/ATLAS-TERRAIN.md, "Class (c)").

import * as THREE from 'three';

import type { BodyRow, Corridor, CorridorBand } from '../world/types';

/** Segments around the shell. The shell is a sky, so the silhouette is the eye's. */
const SHELL_SEGMENTS_W = 48;
const SHELL_SEGMENTS_H = 32;

/**
 * Radiance the lit band's own thickness is worth, and the share the others get.
 *
 * Additive over the globe and the stars, so these are a look and not a physical
 * scattering: what has to be true is that moving from one band to the next
 * changes the frame more than standing still in one does, which is what
 * `descent-dojo --body <giant>`'s `corridor` criterion measures.
 */
const BAND_LIT_RADIANCE = 0.55;
const BAND_DIM_RADIANCE = 0.10;

/** Deck outward. Cool at the deck, warm at the top, so the stack reads as one. */
const BAND_TINTS: readonly [number, number, number] = [0x7fd8e8, 0xc8d8a8, 0xe8b878];

/**
 * Where a label sits, as a fraction of the eye's own horizon angle.
 *
 * Along the view rather than at the zenith, and CLOSE rather than at the limb:
 * three shells 50 to 300 km apart seen from 25,000 km away are within a degree
 * of each other, and at a third of the horizon they spread over about twelve.
 */
const LABEL_HORIZON_FRACTION = 0.35;

/** Label height on screen, degrees. A sprite with attenuation off is angular. */
const LABEL_HEIGHT_DEG = 1.05;

const LABEL_CSS = '#cfe8f2';
const SCOOP_CSS = '#ffd28a';

const CORRIDOR_VERT = /* glsl */ `
varying vec3 vLocal;

#include <common>
#include <logdepthbuf_pars_vertex>

void main() {
  vLocal = position;
  gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
  #include <logdepthbuf_vertex>
}
`;

/**
 * Path length inside each shell along the view ray, clipped at the deck.
 *
 * `spanInside` is the measure of the ray's parameter where it is inside a
 * sphere of radius r, which is the one case that has to be right whether the
 * eye is inside that sphere or outside it: entering the corridor from above,
 * the eye is outside all four radii and each band is a chord; standing at the
 * deck it is inside all four and each band is the difference of two exit
 * distances. A band's own length is then the difference of the two spans, and
 * the deck is the far clip because nothing behind it is air.
 *
 * Every quadratic here takes the eye's HEIGHT above the shell rather than
 * forming `dot(o, o) - r * r` itself. Both terms are 4.9e15 on Jupiter, where
 * one float32 ulp is 5.4e8 - four metres of eye height - and at the settled
 * stop the eye is ON the deck, so the difference is noise whose SIGN decides
 * whether a downward ray is clipped at the deck or integrated through the
 * planet. `uEyeHeights` is that difference taken on the CPU in float64, and
 * the near root is `c / (-b + s)` rather than `-b - s`, which cancels the same
 * way once c is small beside b*b.
 */
const CORRIDOR_FRAG = /* glsl */ `
precision highp float;

uniform vec3 uEyeLocal;
uniform vec4 uRadii;
uniform vec3 uTint0;
uniform vec3 uTint1;
uniform vec3 uTint2;
uniform vec3 uRadiance;
uniform vec3 uNorm;
// The eye's height above each of uRadii's four shells, metres.
uniform vec4 uEyeHeights;
uniform float uDeckRadius;
uniform float uFade;

varying vec3 vLocal;

#include <common>
#include <logdepthbuf_pars_fragment>

/** (r + h)^2 - r^2, from a height differenced before it was squared. */
float shellC(float h, float r) {
  return h * (h + 2.0 * r);
}

float spanInside(vec3 o, vec3 d, float h, float r, float tMax) {
  float b = dot(o, d);
  float c = shellC(h, r);
  float disc = b * b - c;
  if (disc <= 0.0) return 0.0;
  float far = -b + sqrt(disc);
  if (far <= 0.0) return 0.0;
  float t0 = max(c / far, 0.0);
  float t1 = min(far, tMax);
  return max(t1 - t0, 0.0);
}

/** Where the ray meets the opaque deck, or a negative number for never. */
float deckClip(vec3 o, vec3 d, float h, float r) {
  float b = dot(o, d);
  // At or under the deck the direction alone decides: down carries no air at
  // all, up carries everything above the eye.
  if (h <= 0.0) return b < 0.0 ? 0.0 : -1.0;
  if (b >= 0.0) return -1.0;
  float c = shellC(h, r);
  float disc = b * b - c;
  if (disc < 0.0) return -1.0;
  return c / (-b + sqrt(disc));
}

void main() {
  vec3 dir = normalize(vLocal - uEyeLocal);
  // The deck is opaque: a ray that reaches it carries no air behind it.
  float tHit = deckClip(uEyeLocal, dir, uEyeHeights.x, uDeckRadius);
  float tMax = tHit < 0.0 ? 1.0e12 : tHit;
  float s0 = spanInside(uEyeLocal, dir, uEyeHeights.x, uRadii.x, tMax);
  float s1 = spanInside(uEyeLocal, dir, uEyeHeights.y, uRadii.y, tMax);
  float s2 = spanInside(uEyeLocal, dir, uEyeHeights.z, uRadii.z, tMax);
  float s3 = spanInside(uEyeLocal, dir, uEyeHeights.w, uRadii.w, tMax);
  // Saturating on each band's own GRAZING chord, not its thickness: a shell
  // 50 km thick over a 69,911 km deck is 5,300 km of path along the limb and
  // 50 km straight up, so a thickness-normalised band is saturated over the
  // whole sky and the three of them are one flat colour. Normalised on the
  // grazing chord instead, each band peaks at its OWN tangent and the stack
  // reads as three nested arcs.
  float l0 = 1.0 - exp(-(s1 - s0) / uNorm.x);
  float l1 = 1.0 - exp(-(s2 - s1) / uNorm.y);
  float l2 = 1.0 - exp(-(s3 - s2) / uNorm.z);
  vec3 sum = uTint0 * (l0 * uRadiance.x)
           + uTint1 * (l1 * uRadiance.y)
           + uTint2 * (l2 * uRadiance.z);
  gl_FragColor = vec4(sum * uFade, 1.0);
  #include <logdepthbuf_fragment>
}
`;

/** A band's altitudes above the DATUM, which is what the camera reports. */
export interface CorridorBandAltitudes {
  readonly id: string;
  readonly name: string;
  /** Altitude of the band's lower edge above the datum, metres. */
  readonly bottomAltitudeM: number;
  readonly topAltitudeM: number;
  readonly he3MoleFraction: number | null;
  /** `NAME 0-50 km`: the rung's title, derived from the edges and never stored. */
  readonly title: string;
}

/**
 * Every altitude the corridor puts on the camera, from one row.
 *
 * The one derivation: `src/atlas.ts` reads it for the camera floor, the
 * breadcrumb rungs and the honesty overlay, and this view reads it for the
 * shells, so a moved band edge moves all four together.
 */
export interface CorridorGeometry {
  readonly deckAltitudeM: number;
  readonly floorAltitudeM: number;
  readonly topAltitudeM: number;
  readonly scaleHeightM: number;
  readonly scoopAltitudeM: number | null;
  /** Deck outward. */
  readonly bands: readonly CorridorBandAltitudes[];
}

function bandTitle(band: CorridorBand): string {
  const km = (m: number): string => `${Math.round(m / 1000)}`;
  return `${band.name} ${km(band.bottom_m)}-${km(band.top_m)} km`;
}

/**
 * @param liftLowestBottomM added to the lowest band's own bottom edge, metres:
 *   `descent-dojo --inject corridor-edge`'s hook, and 0 everywhere else.
 */
export function corridorGeometry(
  corridor: Corridor, liftLowestBottomM = 0,
): CorridorGeometry {
  const deck = corridor.deck_m;
  const bands = corridor.bands.map((b, i) => {
    const bottom = i === 0 ? b.bottom_m + liftLowestBottomM : b.bottom_m;
    return {
      id: b.id,
      name: b.name,
      bottomAltitudeM: deck + bottom,
      topAltitudeM: deck + b.top_m,
      he3MoleFraction: b.he3_mole_fraction,
      title: bandTitle({ ...b, bottom_m: bottom }),
    };
  });
  return {
    deckAltitudeM: deck,
    floorAltitudeM: bands[0]!.bottomAltitudeM,
    topAltitudeM: bands[bands.length - 1]!.topAltitudeM,
    scaleHeightM: corridor.scale_height_m,
    scoopAltitudeM: corridor.scoop_perijove_m,
    bands,
  };
}

/**
 * Which band an altitude is in: 0 at the deck, null above the corridor's top.
 *
 * Both outer edges are closed - entering at exactly the top edge is the top
 * band and standing on the floor is the lowest one - so the camera is never
 * inside the corridor and in no band at all.
 */
export function corridorBandIndexAt(geo: CorridorGeometry, altitudeM: number): number | null {
  if (altitudeM > geo.topAltitudeM) return null;
  for (let i = geo.bands.length - 1; i >= 0; i--) {
    if (altitudeM >= geo.bands[i]!.bottomAltitudeM) return i;
  }
  return 0;
}

export interface CorridorViewConfig {
  readonly row: BodyRow;
  readonly geometry: CorridorGeometry;
}

export interface CorridorViewHandle {
  /** Parented under the body's own sphere, so the pole quaternion is the body's. */
  readonly group: THREE.Group;
  /** The shell radii the strata are drawn at, body centre outward, metres. */
  readonly shellRadiiM: readonly number[];
  /** The band the last `update` lit, or null with the camera above the top. */
  readonly litBand: number | null;
  /**
   * @param eyeLocal the eye relative to the body centre, in the LOCAL metres
   *   the geometry is in - so divided by whatever scale the orrery drew the
   *   body at, the way `rings.update` takes it.
   * @param viewDirLocal the direction the camera looks, unit, same frame: the
   *   labels stack along it, so they are in the frame at every yaw.
   * @param altitudeM the camera's own altitude above the datum.
   */
  update(eyeLocal: THREE.Vector3, viewDirLocal: THREE.Vector3, altitudeM: number): void;
  setVisible(on: boolean): void;
  readonly visible: boolean;
  dispose(): void;
}

function labelSprite(text: string, css: string): {
  sprite: THREE.Sprite; dispose: () => void;
} | null {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;
  const font = 'bold 34px ui-monospace, SFMono-Regular, Menlo, monospace';
  ctx.font = font;
  canvas.width = Math.ceil(ctx.measureText(text).width) + 24;
  canvas.height = 48;
  // Sizing the canvas resets the context, so the font is set again after it.
  ctx.font = font;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.lineWidth = 6;
  ctx.strokeStyle = 'rgba(4,7,12,0.88)';
  ctx.strokeText(text, canvas.width * 0.5, canvas.height * 0.5);
  ctx.fillStyle = css;
  ctx.fillText(text, canvas.width * 0.5, canvas.height * 0.5);
  const texture = new THREE.CanvasTexture(canvas);
  // A picture, not data: the glyphs are painted in their final colour, which is
  // the `imagery` role `descent-dojo --colour` audits textures under.
  texture.name = 'body-map:corridor-label';
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = false;
  texture.minFilter = THREE.LinearFilter;
  const material = new THREE.SpriteMaterial({
    map: texture, transparent: true, depthTest: false, depthWrite: false,
    sizeAttenuation: false,
  });
  material.name = 'corridor-label';
  const sprite = new THREE.Sprite(material);
  sprite.frustumCulled = false;
  sprite.renderOrder = 4;
  const rad = (LABEL_HEIGHT_DEG * Math.PI) / 180;
  sprite.scale.set(rad * (canvas.width / canvas.height), rad, 1);
  return {
    sprite,
    dispose: () => { texture.dispose(); material.dispose(); },
  };
}

/** `He-3 17.0 ppm`, or what canon does not publish said as such. */
function he3Text(fraction: number | null): string {
  return fraction === null
    ? 'He-3 not published'
    : `He-3 ${(fraction * 1e6).toFixed(1)} ppm`;
}

export function createCorridorView(config: CorridorViewConfig): CorridorViewHandle {
  const { row, geometry } = config;
  const datum = row.radius_m;
  const shellRadii = [
    datum + geometry.bands[0]!.bottomAltitudeM,
    datum + geometry.bands[0]!.topAltitudeM,
    datum + geometry.bands[1]!.topAltitudeM,
    datum + geometry.bands[2]!.topAltitudeM,
  ];
  const group = new THREE.Group();
  group.name = `${row.id}-corridor`;
  group.visible = false;
  group.frustumCulled = false;

  const uniforms: Record<string, THREE.IUniform> = {
    uEyeLocal: { value: new THREE.Vector3(0, 0, shellRadii[3]! * 2) },
    uRadii: {
      value: new THREE.Vector4(shellRadii[0], shellRadii[1], shellRadii[2], shellRadii[3]),
    },
    uTint0: { value: new THREE.Color(BAND_TINTS[0]) },
    uTint1: { value: new THREE.Color(BAND_TINTS[1]) },
    uTint2: { value: new THREE.Color(BAND_TINTS[2]) },
    uRadiance: { value: new THREE.Vector3(BAND_DIM_RADIANCE, BAND_DIM_RADIANCE, BAND_DIM_RADIANCE) },
    // 2*sqrt(2 R t) per band: the chord a ray tangent to the band's own lower
    // edge cuts through it.
    uNorm: {
      value: new THREE.Vector3(
        2 * Math.sqrt(2 * shellRadii[0]! * (shellRadii[1]! - shellRadii[0]!)),
        2 * Math.sqrt(2 * shellRadii[1]! * (shellRadii[2]! - shellRadii[1]!)),
        2 * Math.sqrt(2 * shellRadii[2]! * (shellRadii[3]! - shellRadii[2]!)),
      ),
    },
    uEyeHeights: { value: new THREE.Vector4(1, 1, 1, 1) },
    uDeckRadius: { value: shellRadii[0] },
    uFade: { value: 1 },
  };
  // One mesh at the outermost shell, not one per band: the eye is inside it for
  // most of the corridor, so a back-facing sphere is the whole sky, and the
  // three bands are an analytic span each along the same ray.
  const shellGeo = new THREE.SphereGeometry(
    shellRadii[3], SHELL_SEGMENTS_W, SHELL_SEGMENTS_H);
  const shellMat = new THREE.ShaderMaterial({
    uniforms,
    vertexShader: CORRIDOR_VERT,
    fragmentShader: CORRIDOR_FRAG,
    side: THREE.DoubleSide,
    transparent: true,
    // Air, not a surface. No depth test either: the mesh is a proxy for "cover
    // these pixels" and the radiance on each one is solved along the ray, and
    // the camera is INSIDE the outermost shell for most of the corridor - so
    // the fragments that cover the frame are its far side, past the deck, and
    // a depth test against the deck would discard the whole layer. Physically
    // the air IS in front of the deck along every ray that reaches it.
    depthWrite: false,
    depthTest: false,
    blending: THREE.AdditiveBlending,
  });
  shellMat.name = `corridor:${row.id}`;
  const shell = new THREE.Mesh(shellGeo, shellMat);
  shell.name = `${row.id}-corridor-strata`;
  shell.frustumCulled = false;
  shell.renderOrder = 1;
  group.add(shell);

  const labels: Array<{ sprite: THREE.Sprite; radiusM: number; dispose: () => void }> = [];
  for (const band of geometry.bands) {
    const text = `${band.title}  ${he3Text(band.he3MoleFraction)}`;
    const made = labelSprite(text, LABEL_CSS);
    if (!made) continue;
    labels.push({
      ...made,
      radiusM: datum + (band.bottomAltitudeM + band.topAltitudeM) * 0.5,
    });
    group.add(made.sprite);
  }
  if (geometry.scoopAltitudeM !== null) {
    const made = labelSprite('GENS IOVIA SCOOP  PERIJOVE', SCOOP_CSS);
    if (made) {
      labels.push({ ...made, radiusM: datum + geometry.scoopAltitudeM });
      group.add(made.sprite);
    }
  }

  const _up = new THREE.Vector3();
  const _tan = new THREE.Vector3();
  const _dir = new THREE.Vector3();
  const POLE = new THREE.Vector3(0, 1, 0);
  const XLOCAL = new THREE.Vector3(1, 0, 0);
  let lit: number | null = null;

  return {
    group,
    shellRadiiM: shellRadii,
    get litBand(): number | null { return lit; },
    get visible(): boolean { return group.visible; },
    update(eyeLocal: THREE.Vector3, viewDirLocal: THREE.Vector3, altitudeM: number): void {
      (uniforms['uEyeLocal']!.value as THREE.Vector3).copy(eyeLocal);
      // Off the camera's own altitude and the geometry's own edges, both
      // metres above the datum: the shell radii these are heights above are
      // `datum + edge`, and differencing the datum out here is what keeps the
      // shader off two 4.9e15 terms whose difference is the whole answer.
      (uniforms['uEyeHeights']!.value as THREE.Vector4).set(
        altitudeM - geometry.bands[0]!.bottomAltitudeM,
        altitudeM - geometry.bands[0]!.topAltitudeM,
        altitudeM - geometry.bands[1]!.topAltitudeM,
        altitudeM - geometry.bands[2]!.topAltitudeM,
      );
      lit = corridorBandIndexAt(geometry, altitudeM);
      const rad = uniforms['uRadiance']!.value as THREE.Vector3;
      rad.set(BAND_DIM_RADIANCE, BAND_DIM_RADIANCE, BAND_DIM_RADIANCE);
      if (lit === 0) rad.x = BAND_LIT_RADIANCE;
      else if (lit === 1) rad.y = BAND_LIT_RADIANCE;
      else if (lit === 2) rad.z = BAND_LIT_RADIANCE;
      if (labels.length === 0) return;
      // Ahead of the eye and off its own up, by a fraction of the horizon
      // angle: the stack sits where the camera is already looking at every yaw
      // and every altitude in the corridor, instead of at one of them.
      _up.copy(eyeLocal);
      const rEye = Math.max(_up.length(), 1);
      _up.multiplyScalar(1 / rEye);
      _tan.copy(viewDirLocal).addScaledVector(_up, -viewDirLocal.dot(_up));
      if (_tan.lengthSq() < 1e-12) {
        _tan.copy(Math.abs(_up.dot(POLE)) > 0.999 ? XLOCAL : POLE).cross(_up);
      }
      if (_tan.lengthSq() < 1e-12) _tan.set(1, 0, 0);
      _tan.normalize();
      const horizon = Math.acos(Math.min(1, shellRadii[0]! / rEye));
      const theta = Math.max(1e-4, horizon) * LABEL_HORIZON_FRACTION;
      _dir.copy(_up).multiplyScalar(Math.cos(theta)).addScaledVector(_tan, Math.sin(theta));
      for (const l of labels) l.sprite.position.copy(_dir).multiplyScalar(l.radiusM);
    },
    setVisible(on: boolean): void {
      group.visible = on;
    },
    dispose(): void {
      group.removeFromParent();
      group.clear();
      shellGeo.dispose();
      shellMat.dispose();
      for (const l of labels) l.dispose();
      labels.length = 0;
    },
  };
}
